import React, { useState, useEffect } from 'react';
import { FaSearch, FaCalendarAlt } from 'react-icons/fa';
import { exportToExcel } from '../utils/exportToExcel';
import { transactionsAPI } from '../services/api';
import './Transactions.css';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await transactionsAPI.getAll();
        console.log('API Response:', response);
        
        // Handle the new response format
        if (response.data && response.data.success === true) {
          // Make sure we're accessing the data correctly
          const transactionsData = response.data.data?.transactions || response.data.transactions || response.data || [];
          console.log('Transactions Data Extracted:', transactionsData);
          setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
        } else {
          throw new Error(response.data?.message || 'Failed to fetch transactions');
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTransactions();
  }, []);

  console.log('Current Transactions State:', transactions);

  const filteredTransactions = transactions.filter(transaction => {
    // Check if transaction has required properties
    if (!transaction) return false;
    
    const matchesFilter = filter === 'all' || (transaction.status && transaction.status === filter);
    const matchesSearch = !searchTerm || 
      (transaction.id && transaction.id.toString().includes(searchTerm)) ||
      (transaction.customer_name && transaction.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (transaction.hotel_name && transaction.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matchesDate = true;
    if (dateRange.start && dateRange.end && transaction.date) {
      const transactionDate = new Date(transaction.date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      matchesDate = transactionDate >= startDate && transactionDate <= endDate;
    }
    
    return matchesFilter && matchesSearch && matchesDate;
  });

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'refunded': return 'status-refunded';
      default: return '';
    }
  };

  // Export function
  const exportTransactionsToExcel = () => {
    // Prepare data for export
    const exportData = filteredTransactions.map(transaction => ({
      'Transaction ID': transaction.id || '',
      'Booking ID': transaction.booking_id || '',
      'Customer Name': transaction.customer_name || '',
      'Hotel Name': transaction.hotel_name || '',
      Amount: transaction.amount ? `$${transaction.amount}` : '$0',
      Currency: transaction.currency || '',
      'Payment Method': transaction.payment_method || '',
      Date: transaction.date || '',
      Status: transaction.status ? transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1) : ''
    }));
    
    exportToExcel(exportData, 'transactions', 'Transactions');
  };

  // Handle loading and error states
  if (loading) {
    return <div className="loading">Loading transactions...</div>;
  }

  if (error) {
    return (
      <div className="error">
        Error loading transactions: {error.message}
        <br />
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <div className="page-header">
        <h1>Transactions Management</h1>
        <button className="btn btn-primary" onClick={exportTransactionsToExcel}>
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="transactions-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by transaction ID, customer, or hotel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon"><FaSearch /></span>
        </div>
        
        <div className="filter-box">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Transactions</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        
        <div className="date-range">
          <FaCalendarAlt />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
          />
        </div>
      </div>

      {/* Transactions List */}
      <div className="transactions-list">
        <h2>Transactions List</h2>
        <div style={{ marginBottom: '10px' }}>
          Total transactions: {transactions.length} | Filtered transactions: {filteredTransactions.length}
        </div>
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Booking ID</th>
              <th>Customer</th>
              <th>Hotel</th>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map(transaction => (
                <tr key={transaction.id || Math.random()}>
                  <td>{transaction.id || ''}</td>
                  <td>{transaction.booking_id || ''}</td>
                  <td>{transaction.customer_name || ''}</td>
                  <td>{transaction.hotel_name || ''}</td>
                  <td>{transaction.amount ? `$${transaction.amount}` : '$0'}</td>
                  <td>{transaction.payment_method || ''}</td>
                  <td>{transaction.date ? new Date(transaction.date).toLocaleDateString() : ''}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(transaction.status)}`}>
                      {transaction.status ? transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1) : ''}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center' }}>
                  No transactions found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {transactions.length === 0 && (
          <div className="no-transactions">
            <p>No transactions data available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;